import { useEffect, useState } from 'react';
import {
  createFactoryMiniappEntry,
  factoryMiniappCodeUrl,
  factoryOrdinaryQrUrl,
  getFactoryMiniappEntry,
  revokeFactoryShare,
  rotateFactoryMiniappEntry,
  rotateFactoryShare,
  type FactoryConfig,
  type FactoryExpert,
  type FactoryMiniappEntry,
} from '../../lib/factory-experts-api';
import { FactoryIcon, FactoryToggle, factoryError } from './FactoryPrimitives';
import type { FactoryMutation } from './FactorySkillsTab';
import FactoryPurchaseAgreementConsent from './FactoryPurchaseAgreementConsent';
import FactoryIdentityPanel from './FactoryIdentityPanel';
import type { FactoryIdentityController } from './useFactoryIdentity';

function validWorkbenchButton(config: FactoryConfig, button: FactoryConfig['workbench']['sections'][number]['buttons'][number]) {
  return !!button.name.trim() && (button.action_type === 'skill'
    ? config.skills.some((skill) => skill.id === button.skill_id && skill.enabled && skill.status === 'ready')
    : !!button.prompt.trim());
}

export function factoryWorkbenchIsConfigured(config: FactoryConfig) {
  if (!config.workbench.enabled) return false;
  const populated = config.workbench.sections.filter((section) => section.buttons.length > 0);
  return populated.length > 0
    && populated.every((section) => !!section.name.trim() && section.buttons.every((button) => validWorkbenchButton(config, button)));
}

export function factoryPublishChecks(config: FactoryConfig) {
  const promoterCommissionBps = config.publishing.promoter_commission_bps ?? 2000;
  return [
    { label: '名称、简介与 3 个推荐问题已填写', valid: !!config.display_name.trim() && !!config.description.trim() && config.starter_questions.length === 3 && config.starter_questions.every((question) => question.trim()) },
    { label: '专家文档已填写', valid: !!config.documents['SOUL.md']?.trim() },
    { label: '已启用技能均已就绪', valid: config.skills.every((skill) => !skill.enabled || skill.status === 'ready') },
    { label: '工作台绑定有效', valid: !config.workbench.enabled || factoryWorkbenchIsConfigured(config) },
    { label: '使用方式与套餐有效', valid: !config.publishing.is_paid || config.publishing.plans.some((plan) => plan.enabled && plan.price_fen > 0) && config.publishing.plans.every((plan) => !plan.enabled || plan.price_fen > 0) },
    { label: '推广员设置有效', valid: !config.publishing.promoter_enabled || (promoterCommissionBps >= 100 && promoterCommissionBps <= 7000) },
  ];
}

export function factoryPublishAction(expert: FactoryExpert, config: FactoryConfig) {
  const currentPublishedVersion = !!expert.active_version_id && expert.published_revision === expert.revision;
  if (!config.publishing.is_public) {
    return {
      label: expert.active_version_id ? '发布更新' : '发布专家',
      disabled: false,
      status: expert.active_version_id ? '无需公开审核' : '尚未发布',
      help: '不公开的专家发布后可通过分享链接使用，不进入专家广场。',
    };
  }
  if (currentPublishedVersion && expert.audit_status === 'pending') {
    return { label: '审核中', disabled: true, status: '平台审核中', help: '审核通过后才会在专家广场展示。' };
  }
  if (currentPublishedVersion && expert.audit_status === 'rejected') {
    return { label: '重新提交审核', disabled: false, status: '审核未通过', help: '修改内容后可重新提交平台审核。' };
  }
  if (currentPublishedVersion && expert.audit_status === 'approved') {
    return { label: '已审核通过', disabled: true, status: '审核通过', help: '当前版本已在专家广场展示，更新后需重新审核。' };
  }
  return {
    label: expert.active_version_id ? '提交更新审核' : '提交审核',
    disabled: false,
    status: expert.active_version_id ? '有待提交的更新' : '尚未提交审核',
    help: '提交后由平台审核，审核通过后才会在专家广场展示。',
  };
}
function MiniappEntryCard({ expert, enabled }: { expert: FactoryExpert; enabled: boolean }) {
  const [entry, setEntry] = useState<FactoryMiniappEntry | null>(null);
  const [state, setState] = useState<'idle' | 'loading' | 'rotating'>('idle');
  const [notice, setNotice] = useState('');
  useEffect(() => {
    let cancelled = false;
    setEntry(null);
    setNotice('');
    if (!enabled || !expert.active_version_id || expert.status !== 'published' || !expert.share_url) return;
    setState('loading');
    void getFactoryMiniappEntry(expert.id)
      .then(({ entry: current }) => current ?? createFactoryMiniappEntry(expert.id))
      .then((current) => { if (!cancelled) setEntry(current); })
      .catch((error) => { if (!cancelled) setNotice(factoryError(error)); })
      .finally(() => { if (!cancelled) setState('idle'); });
    return () => { cancelled = true; };
  }, [enabled, expert.active_version_id, expert.id, expert.revision, expert.share_url, expert.status]);

  const copyOrdinary = async () => {
    if (!entry?.ordinary_qr_url) return;
    try {
      await navigator.clipboard.writeText(entry.ordinary_qr_url);
      setNotice('普通二维码地址已复制。');
    } catch (error) {
      setNotice(`无法自动复制，请选中地址复制。${factoryError(error)}`);
    }
  };
  const rotate = async () => {
    setState('rotating');
    setNotice('');
    try {
      setEntry(await rotateFactoryMiniappEntry(expert.id));
      setNotice('已生成新码，旧的小程序码和二维码已失效。');
    } catch (error) {
      setNotice(factoryError(error));
    } finally {
      setState('idle');
    }
  };

  return <section className="fx-card fx-miniapp-entry">
    <div className="fx-card-heading"><div><h3>微信扫码入口</h3><p>发布并开启分享后自动生成；扫码直接进入当前专家。</p></div>{entry && <span className="fx-badge green">{entry.env_version === 'release' ? '正式版' : entry.env_version === 'trial' ? '体验版' : '开发版'}</span>}</div>
    {!enabled || !expert.active_version_id || !expert.share_url
      ? <p className="fx-fine">发布专家并开启分享后，将自动生成小程序码和普通二维码。</p>
      : state === 'loading'
        ? <p className="fx-fine" role="status">正在生成微信扫码入口…</p>
        : entry
          ? <div className="fx-miniapp-code-grid">
              <div><img src={`${factoryMiniappCodeUrl(expert.id)}?v=${encodeURIComponent(entry.updated_at)}`} alt={`${expert.config.display_name} 小程序码`} /><strong>小程序码</strong><a className="fx-button" href={`${factoryMiniappCodeUrl(expert.id)}?download=1`} download>下载小程序码</a></div>
              <div>{entry.ordinary_qr_url ? <img src={`${factoryOrdinaryQrUrl(expert.id)}?v=${encodeURIComponent(entry.updated_at)}`} alt={`${expert.config.display_name} 普通二维码`} /> : <span className="fx-code-placeholder">需配置二维码域名前缀</span>}<strong>普通二维码</strong>{entry.ordinary_qr_url && <a className="fx-button" href={`${factoryOrdinaryQrUrl(expert.id)}?download=1`} download>下载二维码</a>}</div>
            </div>
          : <button className="fx-button" type="button" onClick={() => void rotate()} disabled={state !== 'idle'}>重试生成</button>}
    {entry?.ordinary_qr_url && <div className="fx-share-field"><input aria-label="普通二维码地址" readOnly value={entry.ordinary_qr_url} /><button type="button" className="fx-button" onClick={() => void copyOrdinary()}>复制地址</button></div>}
    {entry && <button className="fx-link" type="button" disabled={state !== 'idle'} onClick={() => void rotate()}>{state === 'rotating' ? '正在重新生成…' : '让旧码失效并重新生成'}</button>}
    {notice && <p role="status" className="fx-notice">{notice}</p>}
  </section>;
}

type Props = {
  config: FactoryConfig;
  expert: FactoryExpert;
  edit: (update: (value: FactoryConfig) => FactoryConfig) => void;
  mutate: FactoryMutation;
  onPublish: () => void;
  onVersions: () => void;
  onSales: () => void;
  agreementAccepted: boolean;
  onAgreementAcceptedChange: (accepted: boolean) => void;
  busy: boolean;
  identity?: FactoryIdentityController;
};

export default function FactoryPublishTab({
  config,
  expert,
  edit,
  mutate,
  onPublish,
  onVersions,
  onSales,
  agreementAccepted,
  onAgreementAcceptedChange,
  busy,
  identity,
}: Props) {
  const [notice, setNotice] = useState('');
  const checks = factoryPublishChecks(config);
  const publishAction = factoryPublishAction(expert, config);
  const identityBlocked = identity != null && (!identity.status || (identity.status.required && identity.status.status !== 'verified'));
  const promoterEnabled = config.publishing.promoter_enabled ?? false;
  const promoterCommissionBps = config.publishing.promoter_commission_bps ?? 2000;
  const update = (changes: Partial<FactoryConfig['publishing']>) => edit((current) => ({
    ...current,
    publishing: { ...current.publishing, ...changes },
  }));

  const copy = async () => {
    if (!expert.share_url) return;
    try {
      await navigator.clipboard.writeText(new URL(expert.share_url, window.location.origin).href);
      setNotice('分享链接已复制。');
    } catch (error) {
      setNotice(`无法自动复制，请选中链接复制。${factoryError(error)}`);
    }
  };

  const togglePromoters = (promoter_enabled: boolean) => {
    const commissionIsValid = promoterCommissionBps >= 100 && promoterCommissionBps <= 7000;
    update({
      promoter_enabled,
      promoter_commission_bps: commissionIsValid ? promoterCommissionBps : 2000,
    });
  };

  const changePromoterCommission = (value: string) => {
    const percent = Number(value);
    if (!Number.isFinite(percent)) return;
    update({ promoter_commission_bps: Math.round(Math.max(0, Math.min(70, percent)) * 100) });
  };

  return (
    <div className="fx-publish">
      <div className="fx-section-title"><h2>发布设置</h2></div>
      <div className="fx-publish-stack">
        {identity && <FactoryIdentityPanel identity={identity} />}
        <section className="fx-card fx-publish-scope">
          <h3>使用范围</h3>
          <div className="fx-publishing-row">
            <strong>是否公开</strong>
            <FactoryToggle label="是否公开" checked={config.publishing.is_public} onChange={(is_public) => update({ is_public })} />
            <div>
              <span>{config.publishing.is_public ? '公开' : '不公开'}</span>
              <p>{config.publishing.is_public ? '公开发布需经平台审核，审核通过后才会显示在专家广场。' : '不公开的专家不会显示在专家广场。'}</p>
            </div>
          </div>
          <div className="fx-publishing-row">
            <strong>分享链接</strong>
            <FactoryToggle label="分享链接" checked={config.publishing.share_enabled} onChange={(share_enabled) => update({ share_enabled })} />
            <div>
              <span>{config.publishing.share_enabled ? '已开启' : '未开启'}</span>
              <p>分享对象需登录，付费专家需先购买。</p>
              {expert.share_url ? (
                <div className="fx-share-field">
                  <input aria-label="专家分享链接" readOnly value={new URL(expert.share_url, window.location.origin).href} />
                  <button type="button" className="fx-button" onClick={() => void copy()}>复制链接</button>
                  <button type="button" className="fx-button" disabled={busy} onClick={() => void mutate((saved) => revokeFactoryShare(saved.id, saved.revision), '正在撤销分享…')}>撤销分享</button>
                </div>
              ) : <p className="fx-fine">{expert.active_version_id ? '暂无有效分享链接。' : '发布并开启分享后生成链接。'}</p>}
              {expert.active_version_id && (
                <button className="fx-link" type="button" disabled={busy} onClick={() => void mutate((saved) => rotateFactoryShare(saved.id, saved.revision), '正在生成新的分享链接…')}>生成新的分享链接</button>
              )}
            </div>
          </div>
          <div className="fx-publishing-row fx-promoter-row">
            <strong>推广员功能</strong>
            <FactoryToggle label="推广员功能" checked={promoterEnabled} onChange={togglePromoters} />
            <div>
              <span>{promoterEnabled ? '已开通' : '未开通'}</span>
              <p>开通后，推广员可申请推广该智能体；关闭后不再接受新的推广申请。</p>
              {promoterEnabled && (
                <label className="fx-promoter-rate">
                  <span>推广员分成比例</span>
                  <span className="fx-promoter-rate-input">
                    <input
                      aria-label="推广员分成比例"
                      type="number"
                      min="1"
                      max="70"
                      step="1"
                      value={promoterCommissionBps / 100}
                      onChange={(event) => changePromoterCommission(event.target.value)}
                    />
                    <span>%</span>
                  </span>
                  <small>按订单实付金额计算，比例需在 1%–70% 之间。</small>
                </label>
              )}
            </div>
          </div>
          <p className="fx-fine">范围设置随发布生效；撤销或重新生成链接会立即生效。</p>
          {notice && <p role="status" className="fx-notice">{notice}</p>}
        </section>

        <MiniappEntryCard expert={expert} enabled={config.publishing.share_enabled} />

        <section className="fx-card fx-publish-checks fx-publish-checks-wide">
          <div className="fx-publish-checks-strip">
            <h3>发布检查</h3>
            <ul>
              {checks.map((check) => (
                <li key={check.label} className={check.valid ? 'is-valid' : ''}>
                  <span>{check.valid ? <FactoryIcon name="check" /> : '·'}</span>
                  {check.label}
                </li>
              ))}
            </ul>
          </div>
          <hr />
          <div className="fx-publish-finalize">
            <div className="fx-publish-version-column">
              <h4>版本信息</h4>
              <div className="fx-version-info">
                <span>{expert.published_revision ? `正式版本 r${expert.published_revision}` : '尚未发布'}</span>
                <span className={`fx-badge ${expert.audit_status === 'approved' ? 'green' : expert.audit_status === 'rejected' ? 'red' : 'amber'}`}>{publishAction.status}</span>
              </div>
              <p className="fx-muted">{publishAction.help}</p>
              {expert.active_version_id ? (
                <div className="fx-publish-assets">
                  <h4>发布资产</h4>
                  {expert.ilink_url ? <a className="fx-link" href={expert.ilink_url} target="_blank" rel="noreferrer">打开 iLink 页面</a> : <span className="fx-fine">iLink 页面正在生成</span>}
                  {expert.poster_url ? <a className="fx-link" href={expert.poster_url} target="_blank" rel="noreferrer">查看分享海报</a> : <span className="fx-fine">分享海报正在生成</span>}
                </div>
              ) : null}
              <p className="fx-muted">取消公开、撤销分享或停止销售，不影响已购用户有效权益。</p>
              <button className="fx-link" type="button" onClick={onSales}><FactoryIcon name="file" />查看销售记录 →</button>
            </div>
            <div className="fx-publish-submit-column">
              <FactoryPurchaseAgreementConsent accepted={agreementAccepted} disabled={busy || publishAction.disabled} onChange={onAgreementAcceptedChange} />
              <div className="fx-publish-actions">
                <button className="fx-button fx-primary" type="button" disabled={busy || publishAction.disabled || !agreementAccepted || identityBlocked} onClick={onPublish}><FactoryIcon name="upload" />{publishAction.label}</button>
                <button className="fx-button" type="button" onClick={onVersions}><FactoryIcon name="history" />版本记录</button>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
